import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/spinner";
import { formatTimestampToString } from "@/lib/dates/dateUtils";
import { useGetAllDatevExports } from "@/queries/exports/get-all";
import { Filter, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type ExportFilterBadgeProps = {
  exportId: string | undefined;
  onClear: () => void;
};

export const ExportFilterBadge = ({
  exportId,
  onClear,
}: ExportFilterBadgeProps) => {
  const { t } = useTranslation();

  const { data, loading } = useGetAllDatevExports({
    page: 1,
    limit: 50,
    entityType: "bank-transaction",
    exportId,
  });

  const selectedExport = useMemo(() => {
    if (!exportId || !data) return null;
    return data.find((exp) => exp.datevExportId === exportId);
  }, [exportId, data]);

  if (!exportId) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm w-fit">
        <LoadingSpinner className="w-4 h-4" />
        <span>{t("loading")}...</span>
      </div>
    );
  }

  if (!selectedExport) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm w-fit">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4" />
          <span className="font-medium">{t("filtered_by_export")}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="h-5 w-5 p-0 hover:bg-blue-100"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    );
  }

  const isNotBooked = selectedExport.stackName === "not_booked";

  const getExportLabel = () => {
    if (isNotBooked) {
      return `${t("export_from")} ${formatTimestampToString(
        selectedExport.exportedAt,
      )} • ${t("todos.sort")}`;
    }
    return `${t("export_from")} ${formatTimestampToString(
      selectedExport.exportedAt,
    )} ${
      selectedExport.stackName
        ? `• ${t("stack")} ${selectedExport.stackName}`
        : ""
    }`;
  };

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-700 text-sm w-fit max-w-full">
      <div className="flex items-center gap-2 min-w-0">
        <Filter className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium">{t("filtered_by_export")}:</span>
        <span className="truncate">
          {getExportLabel()} ({selectedExport.entityCounts.total}{" "}
          {t("transactions.label", {
            count: selectedExport.entityCounts.total,
          })}
          )
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        className="h-5 w-5 p-0 hover:bg-blue-100 flex-shrink-0"
        title={t("clear_filter")}
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
};
